<template>
  <NuxtLink
    :to="toPath"
    :target="linkTarget"
    class="experience-card"
    :style="{ width: cardWidth }"
  >
    <NuxtImg loading="lazy" v-no-right-click :src="image" :alt="title" height="220" />
    <div class="content">
      <div class="flex mb-[6px] justify-between">
        <div v-if="!hideCategoryAndCity" class="text-mobile-heading-6 text-gray-1">
          {{ categoryHandler(categories) }}
        </div>
        <div v-if="rate" class="flex items-center text-primary-2">
          <icon-star-1
            class="star !text-star-color text-[14px] ltr:mr-1 rtl:ml-1"
          />
          <span class="rate-text text-heading-6 !font-bold text-star-color">
            {{ rate }}/5
          </span>
        </div>
      </div>
      <div class="card-title">
        <h3 :title="title" class="text-title-color">
          {{ title }}
        </h3>
        <span
          v-if="isDeferredPaymentEligible(hasPayLater, depositPercent)"
          class="inline-flex items-center gap-[6px] bg-[#f2eded] rounded-[6px] px-[10px] py-[6px] mt-1.5"
        >
          <icon-dollar-sign-circle 
            class="text-[13px] !mb-0"
            :class="depositPercent <= 0 ? 'text-[#15B089]' : 'text-gray-2'"
          />
          <span class="text-[12px] font-extrabold whitespace-nowrap">
            <template v-if="depositPercent <= 0">
              <span class="text-[#15B089]">{{ t("payment.bookNowPayLater") }}</span>
            </template>
            <template v-else>
              <span class="text-gray-2">{{ t("payment.reserveWith") }}</span>
              <span class="text-primary-2">{{ formatPrice(depositAmount) + " " + currency }}</span>
            </template>
          </span>
        </span>
        <div
          v-if="cities.length > 0 && !hideCategoryAndCity"
          class="flex flex-wrap"
          :class="{ 'mt-1.5': !isDeferredPaymentEligible(hasPayLater, depositPercent) }"
        >
          <span v-for="item in cities" :key="item.id" class="city">
            {{ item.name }}
          </span>
        </div>
      </div>
      <div class="rating-and-price">
        <div class="price" :class="{ discount: offPercent > 0 }">
          <span
            v-if="!isNegotiable"
            class="title"
            :class="
              offPercent > 0
                ? '!text-discount-background'
                : '!text-mobile-paragraph-1'
            "
          >
            <span class="lowercase pr-1">
              {{ t("from") }}
            </span>
            <span v-if="offPercent > 0" class="real-price">
              {{ formatPrice(marketPrice) + " " + currency }}
            </span>
            <span v-if="hasDiscount && offPercent > 0" class="discount-percent">
              {{ t("save") }}
              {{ offPercent }}%
            </span>
          </span>
          <span class="final-price">
            {{
              isNegotiable
                ? t("requestDetails")
                : formatPrice(price) + " " + currency
            }}
            <ProductCardAvailabilityType
              v-if="!isNegotiable && !hideAvailabilityType"
              :pricing-type="pricingType"
              :availability-type="availabilityType"
              :duration="duration"
            />
          </span>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import type { PropType } from "vue";
import type { ICity, ICategory } from "../../types/general";
import {
  formatPrice,
  offPercentCalculator,
  categoryHandler,
  isDeferredPaymentEligible,
  getDepositAmount,
} from "../../composables/helper";

const { t } = useI18n();
const route = useRoute();
const props = defineProps({
  width: { type: Number, required: false, default: null },
  image: { type: String, required: true },
  cities: { type: Array as PropType<ICity[]>, required: true },
  title: { type: String, required: true },
  rate: { type: Number, default: null },
  marketPrice: { type: Number, required: false, default: null }, //price before off - if product has off
  price: { type: Number, required: true, default: null },
  currency: { type: String, required: true },
  toPath: { type: String, default: "/" },
  slug: { type: String, required: true },
  hasBorder: { type: Boolean, default: false },
  discountColors: {
    type: String,
    default: "bg-discount-background text-primary-1",
  },
  categories: {
    type: Array as PropType<string[] | ICategory[]>,
    required: true,
  },
  isNegotiable: { type: Boolean, default: false },
  hasPayLater: { type: Boolean, default: false },
  depositPercent: { type: Number, default: 0 },
  pricingType: { type: String, default: null },
  availabilityType: { type: String, default: null },
  duration: { type: Number, default: null },
  hideCategoryAndCity: { type: Boolean, default: false },
  hideAvailabilityType: { type: Boolean, default: false }
});

const offPercent = ref(offPercentCalculator(props.marketPrice, props.price));
const depositAmount = computed(() => getDepositAmount(props.price, props.depositPercent));
const isdiscover = ref(false);
const localePath = useLocalePath();
watch(
  () => route.path,
  () => {
    isdiscover.value = route.path == localePath("/things-to-do");
  },
  { immediate: true }
);
const cardWidth = computed(() => {
  return props.width ? props.width + "px" : null;
});

const hasDiscount = computed(() => {
  if (props.marketPrice && props.marketPrice > props.price) return true;
  return false;
});
const isDesktop = ref(false);
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 1024;
};
onMounted(() => {
  handleResize();
});
const linkTarget = computed(() => {
  return isDesktop.value ? "_blank" : "_self";
});
</script>

<style lang="scss" scoped>
.experience-card {
  @apply block relative rounded-[10px] top-0 hover:md:-top-1 transition-all duration-[250ms];
}
.experience-card img {
  @apply w-full object-cover rounded-[5px] h-[220px];
}

.experience-card .content {
  @apply flex flex-col justify-between  py-2.5;
}

.experience-card .card-title {
  @apply flex flex-col justify-start mb-0.5;
}

.experience-card .card-title .city {
  @apply text-[12px] font-normal text-gray-1 mb-1 mr-1 whitespace-nowrap;
  &::after {
    content: ",";
  }
  &:last-child {
    &::after {
      content: "";
    }
  }
}

.experience-card .card-title h4 {
  @apply text-mobile-heading-4 lg:text-heading-4 text-title-color;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.experience-card .rating-and-price {
  @apply flex flex-col justify-start mt-[15px];
}

.experience-card .rating-and-price .rate-text {
  @apply text-gray-1 xs:text-mobile-paragraph-1 lg:text-paragraph-1;
}

.experience-card .rating-and-price .price .title {
  @apply text-paragraph-2 text-gray-1;
}

.experience-card .rating-and-price .price .final-price {
  @apply text-mobile-heading-5 text-paragraph-color whitespace-nowrap;
}

.experience-card .rating-and-price .price.discount {
  @apply xs:max-sm:flex xs:max-sm:flex-col xs:max-sm:justify-start;
}

.experience-card .rating-and-price .price.discount .title {
  @apply text-paragraph-2;
}

.experience-card .rating-and-price .price .real-price {
  @apply mr-1.5 rtl:ml-1.5 relative text-paragraph-color line-through;
}

.experience-card .discount-percent {
  @apply rounded-[3px] text-[12px] px-[6px] py-[3px] bg-discount-background text-white;
}
</style>
